import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Loader2, AlertTriangle, Clock, Check, CheckCircle2,
  Download, FileText, Shield, X, ArrowDown,
} from 'lucide-react';
import { pdfjsLib } from '../lib/pdfWorker';
import PageCanvas from '../components/PageCanvas';
import SignaturePanel from '../components/SignaturePanel';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { setLanguage, SUPPORTED_CODES, LANGUAGE_STORAGE_KEY } from '../lib/i18n';
import {
  getSignerMeta,
  getSignerDocument,
  submitSignature,
  declineSignature,
  getCompletedDownload,
  getCertificateDownload,
} from '../lib/esignApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ESIGN_CONSENT_TEXT =
  'By checking this box and signing this document electronically, I agree that ' +
  'my electronic signature is the legal equivalent of my manual signature and ' +
  'has the same validity and meaning as a handwritten signature, in accordance ' +
  'with the U.S. Electronic Signatures in Global and National Commerce Act ' +
  '(ESIGN) and the Uniform Electronic Transactions Act (UETA).';

const ESIGN_CONSENT_VERSION = '1.0';

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function todayString() {
  return new Date().toLocaleDateString('en-US');
}

// Powered-by footer used in every phase
function PoweredFooter() {
  return (
    <footer className="py-4 text-center text-xs text-gray-400 select-none">
      Powered by{' '}
      <a
        href="https://scrivar.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-500 hover:text-indigo-600 transition-colors"
      >
        Scrivar
      </a>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Error / terminal screens
// ---------------------------------------------------------------------------

function ErrorScreen({ icon: Icon, title, description }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-5">
            <Icon className="w-7 h-7 text-gray-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{title}</h1>
          <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
        </div>
      </div>
      <PoweredFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
      <PoweredFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success screen (after signing)
// ---------------------------------------------------------------------------

function SuccessScreen({ token, envelopeStatus }) {
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);

  const isComplete = envelopeStatus === 'completed';

  async function handleDownloadPdf() {
    setDownloadingPdf(true);
    try {
      const { url } = await getCompletedDownload(token);
      window.open(url, '_blank');
    } catch { /* not available yet */ }
    setDownloadingPdf(false);
  }

  async function handleDownloadCert() {
    setDownloadingCert(true);
    try {
      const { url } = await getCertificateDownload(token);
      window.open(url, '_blank');
    } catch { /* not available yet */ }
    setDownloadingCert(false);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          {/* animated checkmark */}
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5 animate-[scaleIn_0.35s_ease-out]">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Thanks &mdash; you&rsquo;re done!</h1>
          <p className="text-sm text-gray-500 mb-6">Your signature has been recorded.</p>

          {isComplete && (
            <div className="space-y-3 mb-6">
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download signed document
              </button>
              <button
                onClick={handleDownloadCert}
                disabled={downloadingCert}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {downloadingCert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Download audit certificate
              </button>
            </div>
          )}

          {!isComplete && (
            <p className="text-xs text-gray-400 mb-6">
              The document is still waiting for other signers. You&rsquo;ll receive the
              completed document by email once everyone has signed.
            </p>
          )}

          <p className="text-xs text-gray-400">You can close this window.</p>
        </div>
      </div>
      <PoweredFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Declined confirmation
// ---------------------------------------------------------------------------

function DeclinedScreen() {
  return (
    <ErrorScreen
      icon={X}
      title="You've declined to sign this document"
      description="The sender has been notified. You can close this window."
    />
  );
}

// ---------------------------------------------------------------------------
// Decline modal
// ---------------------------------------------------------------------------

function DeclineModal({ token, onDeclined, onCancel }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const declineBtnRef = useRef(null);

  async function handleDecline() {
    if (reason.trim().length < 3 || reason.trim().length > 500) return;
    setSubmitting(true);
    setError(null);
    try {
      await declineSignature(token, { reason: reason.trim() });
      onDeclined();
    } catch (err) {
      setError(err.message || 'Failed to decline. Please try again.');
    }
    setSubmitting(false);
  }

  // Focus the reason textarea on open and trap Tab + close on Escape.
  // eSign is a legal consent flow — keyboard-only users must be able to
  // decline without reaching for the mouse.
  useEffect(() => {
    const prev = document.activeElement;
    textareaRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onCancel?.();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = [textareaRef.current, cancelBtnRef.current, declineBtnRef.current].filter(Boolean);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      prev?.focus?.();
    };
  }, [submitting, onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="decline-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 id="decline-dialog-title" className="text-lg font-semibold text-gray-900 mb-1">Decline to sign</h2>
        <p className="text-sm text-gray-500 mb-4">Please provide a reason for declining.</p>

        <textarea
          ref={textareaRef}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder="Your reason for declining..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
        />
        <p className="text-xs text-gray-400 mt-1 mb-4">
          {reason.trim().length}/500 characters {reason.trim().length > 0 && reason.trim().length < 3 && '(minimum 3)'}
        </p>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button
            ref={cancelBtnRef}
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            ref={declineBtnRef}
            onClick={handleDecline}
            disabled={submitting || reason.trim().length < 3}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Decline'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signature panel overlay (mobile-friendly)
// ---------------------------------------------------------------------------

function SignaturePanelOverlay({ onCreated, onClose, fieldKind, applyAllCount }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl sm:shadow-xl rounded-t-2xl max-h-[90vh] overflow-auto">
        <SignaturePanel
          onCreated={(dataUrl, opts) => {
            onCreated(dataUrl, opts);
            onClose();
          }}
          onClose={onClose}
          fieldKind={fieldKind}
          applyAllCount={applyAllCount}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field overlay components
// ---------------------------------------------------------------------------

function SignatureFieldDisplay({ value, label, required, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`absolute inset-0 flex items-center justify-center cursor-pointer rounded transition-colors ${
        value
          ? 'border-2 border-emerald-400 bg-emerald-50/50'
          : required
          ? 'border-2 border-dashed border-red-400 bg-red-50/40 hover:bg-red-50/70'
          : 'border-2 border-dashed border-gray-300 bg-gray-50/40 hover:bg-gray-100/60'
      }`}
    >
      {value ? (
        <img src={value} alt="Signature" className="max-w-full max-h-full object-contain p-1" />
      ) : (
        <span className="text-xs font-medium text-gray-500">
          {label || 'Click to sign'}
        </span>
      )}
    </button>
  );
}

function DateFieldDisplay({ value, onChange, required }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => { if (!value) onChange(todayString()); }}
      placeholder="mm/dd/yyyy"
      className={`absolute inset-0 w-full h-full px-2 text-sm bg-transparent rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        value
          ? 'border-2 border-emerald-400'
          : required
          ? 'border-2 border-dashed border-red-400 bg-red-50/40'
          : 'border-2 border-dashed border-gray-300 bg-gray-50/40'
      }`}
    />
  );
}

function TextFieldDisplay({ value, onChange, required, label }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={label || 'Type here...'}
      className={`absolute inset-0 w-full h-full px-2 text-sm bg-transparent rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        value
          ? 'border-2 border-emerald-400'
          : required
          ? 'border-2 border-dashed border-red-400 bg-red-50/40'
          : 'border-2 border-dashed border-gray-300 bg-gray-50/40'
      }`}
    />
  );
}

function CheckboxFieldDisplay({ value, onChange, required }) {
  const checked = value === 'true' || value === true;
  return (
    <button
      type="button"
      onClick={() => onChange(checked ? '' : 'true')}
      className={`absolute inset-0 flex items-center justify-center rounded cursor-pointer transition-colors ${
        checked
          ? 'border-2 border-emerald-400 bg-emerald-50'
          : required
          ? 'border-2 border-dashed border-red-400 bg-red-50/40 hover:bg-red-50/70'
          : 'border-2 border-dashed border-gray-300 bg-gray-50/40 hover:bg-gray-100/60'
      }`}
    >
      {checked && <Check className="w-5 h-5 text-emerald-600" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Welcome gate
// ---------------------------------------------------------------------------

function WelcomeGate({ meta, onContinue }) {
  const { t } = useTranslation();
  const [name, setName] = useState(meta.recipient.name || '');
  const [consented, setConsented] = useState(false);
  const [consentTimestamp, setConsentTimestamp] = useState(null);

  function handleConsentChange(checked) {
    setConsented(checked);
    setConsentTimestamp(checked ? new Date().toISOString() : null);
  }

  const canContinue = name.trim().length > 0 && consented;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top-right language switcher (Prompt 26) */}
      <div className="px-4 py-3 flex justify-end">
        <LanguageSwitcher variant="globe" />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
          {/* heading */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{t('esign:signer.title')}</h1>
              <p className="text-sm text-gray-500">{t('esign:signer.from')} {meta.envelope.sender_name}</p>
            </div>
          </div>

          {/* envelope subject */}
          <p className="font-semibold text-gray-900 mb-2">{meta.envelope.subject}</p>

          {/* message */}
          {meta.envelope.message && (
            <blockquote className="border-l-4 border-gray-200 pl-4 py-2 text-sm text-gray-600 italic bg-gray-50 rounded-r mb-4">
              {meta.envelope.message}
            </blockquote>
          )}

          {/* expiration */}
          {meta.envelope.expires_at && (
            <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-5">
              <Clock className="w-3.5 h-3.5" />
              Expires {formatDate(meta.envelope.expires_at)}
            </p>
          )}

          <hr className="border-gray-200 mb-5" />

          {/* name */}
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('esign:compose.recipients.name')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('esign:compose.recipients.name')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />

          {/* ESIGN consent — legal text kept in English to preserve ESIGN/UETA wording */}
          <blockquote className="border-l-4 border-indigo-200 pl-4 py-3 text-xs text-gray-600 leading-relaxed bg-indigo-50/50 rounded-r mb-4">
            {ESIGN_CONSENT_TEXT}
          </blockquote>

          <label className="flex items-start gap-3 cursor-pointer mb-6 select-none">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => handleConsentChange(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              {t('esign:signer.consent')}
            </span>
          </label>

          {/* continue */}
          <button
            onClick={() => onContinue({ name: name.trim(), consentTimestamp })}
            disabled={!canContinue}
            className="w-full py-2.5 px-4 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('esign:signer.continue')}
          </button>
        </div>
      </div>
      <PoweredFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document view + field filling (main signing phase)
// ---------------------------------------------------------------------------

function DocumentView({ meta, token, consentTimestamp, signerName }) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [sigPanelField, setSigPanelField] = useState(null); // field id of the sig/initials being filled
  // Field ids flashed by the "just applied" pulse (Prompt 24.1 §2a+2d).
  // A set, not a single id, so "Apply to all" can pulse every target at once.
  // Cleared on a timer so a reused saved signature still triggers the anim.
  const [justAppliedIds, setJustAppliedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [envelopeStatus, setEnvelopeStatus] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const scrollContainerRef = useRef(null);
  const fieldRefs = useRef({});
  const pulseTimerRef = useRef(null);

  const fields = meta.fields || [];

  // Group fields by page
  const fieldsByPage = useMemo(() => {
    const map = {};
    for (const f of fields) {
      if (!map[f.page_num]) map[f.page_num] = [];
      map[f.page_num].push(f);
    }
    return map;
  }, [fields]);

  // Compute progress
  const requiredFields = useMemo(() => fields.filter((f) => f.required), [fields]);
  const filledRequiredCount = useMemo(
    () => requiredFields.filter((f) => {
      const v = fieldValues[f.id];
      return v !== undefined && v !== null && String(v).trim() !== '';
    }).length,
    [requiredFields, fieldValues]
  );
  const allRequiredFilled = filledRequiredCount === requiredFields.length;

  // Total filled (including optional)
  const totalFilled = useMemo(
    () => fields.filter((f) => {
      const v = fieldValues[f.id];
      return v !== undefined && v !== null && String(v).trim() !== '';
    }).length,
    [fields, fieldValues]
  );

  // Fetch the PDF on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const blob = await getSignerDocument(token);
        const ab = await blob.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: ab }).promise;
        if (!cancelled) setPdfDoc(doc);
      } catch (err) {
        if (!cancelled) setPdfError(err.message || 'Failed to load document.');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Initialize field values with defaults. Literal 'null'/'undefined' strings
  // can land in the DB if a prior version stringified a nullish value — treat
  // those as empty so the field doesn't appear pre-filled.
  useEffect(() => {
    const initial = {};
    const isEmpty = (v) =>
      v === undefined || v === null || v === 'null' || v === 'undefined' || v === '';
    for (const f of fields) {
      if (!isEmpty(f.value)) {
        initial[f.id] = f.value;
      } else if (!isEmpty(f.default_value)) {
        initial[f.id] = f.default_value;
      }
    }
    setFieldValues(initial);
  }, [fields]);

  // Update a field value
  const updateField = useCallback((fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // Flash the confirmation pulse on one-or-more fields for 200ms. Used when
  // the signer applies a value via the signature panel — without a visible
  // signal the overlay update looks like nothing happened.
  const pulseFields = useCallback((fieldIds) => {
    const ids = Array.isArray(fieldIds) ? fieldIds.filter(Boolean) : [fieldIds].filter(Boolean);
    if (ids.length === 0) return;
    setJustAppliedIds(new Set(ids));
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setJustAppliedIds(new Set());
      pulseTimerRef.current = null;
    }, 220);
  }, []);

  useEffect(() => () => {
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
  }, []);

  // Scroll to the next unfilled required field
  function scrollToNextRequired() {
    const next = requiredFields.find((f) => {
      const v = fieldValues[f.id];
      return v === undefined || v === null || String(v).trim() === '';
    });
    if (next && fieldRefs.current[next.id]) {
      fieldRefs.current[next.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Submit
  async function handleSubmit() {
    // Guard against a fast double-click between the first submit and
    // the re-render to SuccessScreen. The Submit button is disabled
    // while `submitting` is true, but the `finally` that clears
    // `submitting` runs before React unmounts us to the terminal state,
    // opening a brief window where a second tap can fire.
    if (submitting || submitted) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        consent: {
          version: ESIGN_CONSENT_VERSION,
          text: ESIGN_CONSENT_TEXT,
          checked_at: consentTimestamp,
        },
        fields: fields
          .filter((f) => fieldValues[f.id] !== undefined && fieldValues[f.id] !== null)
          .map((f) => ({ id: f.id, value: fieldValues[f.id] })),
      };
      const result = await submitSignature(token, body);
      setEnvelopeStatus(result.envelope_status);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Submission failed. Please try again.');
      setSubmitting(false);
      return;
    }
    // On success, leave `submitting = true` so the button stays disabled
    // until we unmount to SuccessScreen on the next render.
  }

  // Terminal states
  if (submitted) {
    return <SuccessScreen token={token} envelopeStatus={envelopeStatus} />;
  }
  if (declined) {
    return <DeclinedScreen />;
  }

  // Loading PDF
  if (pdfLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading document...</p>
          </div>
        </div>
        <PoweredFooter />
      </div>
    );
  }

  if (pdfError) {
    return <ErrorScreen icon={AlertTriangle} title="Failed to load document" description={pdfError} />;
  }

  const pageCount = pdfDoc?.numPages || 0;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
        <div className="min-w-0">
          <h1 className="text-sm font-semibold text-gray-900 truncate">{meta.envelope.subject}</h1>
          <p className="text-xs text-gray-500">Signing as {signerName}</p>
        </div>
        <button
          onClick={() => setShowDeclineModal(true)}
          className="text-xs text-gray-400 hover:text-red-600 transition-colors whitespace-nowrap ml-4"
        >
          Decline to sign
        </button>
      </header>

      {/* Document pages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto pb-24">
        <div className="max-w-3xl mx-auto py-4 px-2 sm:px-4 space-y-4">
          {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNum) => (
            <div key={pageNum} className="relative bg-white shadow rounded-lg overflow-hidden">
              <PageCanvas pdfDoc={pdfDoc} pageNumber={pageNum} scale={1.2} className="bg-white w-full" />

              {/* Field overlays for this page */}
              {(fieldsByPage[pageNum] || []).map((field) => {
                const value = fieldValues[field.id];
                return (
                  <div
                    key={field.id}
                    ref={(el) => { fieldRefs.current[field.id] = el; }}
                    data-just-applied={justAppliedIds.has(field.id) ? 'true' : undefined}
                    style={{
                      position: 'absolute',
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      width: `${field.width}%`,
                      height: `${field.height}%`,
                    }}
                  >
                    {/* Required badge */}
                    {field.required && !value && (
                      <span className="absolute -top-5 left-0 text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                        Required
                      </span>
                    )}

                    {(field.kind === 'signature' || field.kind === 'initials') && (
                      <SignatureFieldDisplay
                        value={value}
                        label={field.label || (field.kind === 'initials' ? 'Click to initial' : 'Click to sign')}
                        required={field.required}
                        onClick={() => setSigPanelField(field.id)}
                      />
                    )}

                    {field.kind === 'date' && (
                      <DateFieldDisplay
                        value={value}
                        onChange={(v) => updateField(field.id, v)}
                        required={field.required}
                      />
                    )}

                    {field.kind === 'text' && (
                      <TextFieldDisplay
                        value={value}
                        onChange={(v) => updateField(field.id, v)}
                        required={field.required}
                        label={field.label}
                      />
                    )}

                    {field.kind === 'checkbox' && (
                      <CheckboxFieldDisplay
                        value={value}
                        onChange={(v) => updateField(field.id, v)}
                        required={field.required}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Sticky bottom toolbar */}
      <div className="sticky bottom-0 z-30 bg-white border-t border-gray-200 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            <span className="font-medium text-gray-700">{totalFilled}</span>{' '}
            of {fields.length} field{fields.length !== 1 ? 's' : ''} completed
          </div>

          <div className="flex items-center gap-2">
            {!allRequiredFilled && (
              <button
                onClick={scrollToNextRequired}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
              >
                <ArrowDown className="w-3.5 h-3.5" />
                Next field
              </button>
            )}

            <button
              onClick={handleSubmit}
              disabled={!allRequiredFilled || submitting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Submit
            </button>
          </div>
        </div>

        {submitError && (
          <p className="max-w-3xl mx-auto text-xs text-red-600 mt-2">{submitError}</p>
        )}
      </div>

      {/* Signature panel overlay */}
      {sigPanelField && (() => {
        const targetField = fields.find((f) => f.id === sigPanelField);
        const targetKind = targetField?.kind;
        // Count the signer's other fields of the same kind — if > 1, the panel
        // offers "Apply to all" on each saved library row so long contracts
        // don't need per-field clicks.
        const siblings = targetKind
          ? fields.filter((f) => f.kind === targetKind)
          : [];
        const applyAllCount = siblings.length;
        return (
          <SignaturePanelOverlay
            onCreated={(dataUrl, opts) => {
              if (opts?.applyToAll && applyAllCount > 1) {
                const ids = siblings.map((f) => f.id);
                setFieldValues((prev) => {
                  const next = { ...prev };
                  for (const id of ids) next[id] = dataUrl;
                  return next;
                });
                pulseFields(ids);
              } else {
                updateField(sigPanelField, dataUrl);
                pulseFields(sigPanelField);
              }
            }}
            onClose={() => setSigPanelField(null)}
            fieldKind={targetKind}
            applyAllCount={applyAllCount}
          />
        );
      })()}

      {/* Decline modal */}
      {showDeclineModal && (
        <DeclineModal
          token={token}
          onDeclined={() => setDeclined(true)}
          onCancel={() => setShowDeclineModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component — orchestrates phases
// ---------------------------------------------------------------------------

export default function SignDocument() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Phase state: 'welcome' | 'document'
  const [phase, setPhase] = useState('welcome');
  const [signerName, setSignerName] = useState('');
  const [consentTimestamp, setConsentTimestamp] = useState(null);

  // Fetch meta on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSignerMeta(token);
        if (!cancelled) {
          setMeta(data);
          // Switch the signer view to the envelope's language unless the
          // signer has already overridden it on this device via the globe.
          const envelopeLang = data?.envelope?.language;
          if (envelopeLang && SUPPORTED_CODES.includes(envelopeLang)) {
            let alreadyPicked = false;
            try { alreadyPicked = !!window.localStorage.getItem(LANGUAGE_STORAGE_KEY); } catch {}
            if (!alreadyPicked) setLanguage(envelopeLang);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Phase 1 - Loading
  if (loading) return <LoadingScreen />;

  // Phase 2 - Error states
  if (error) {
    if (error.status === 410 || error.body?.code === 'TOKEN_EXPIRED') {
      return (
        <ErrorScreen
          icon={Clock}
          title="This signing link has expired"
          description="The link you followed is no longer valid. Please contact the sender to request a new signing link."
        />
      );
    }
    if (error.status === 404) {
      return (
        <ErrorScreen
          icon={AlertTriangle}
          title="Signing link not found"
          description="This link may be invalid or the document may have been recalled. Please contact the sender for assistance."
        />
      );
    }
    return (
      <ErrorScreen
        icon={AlertTriangle}
        title="Something went wrong"
        description={error.message || 'An unexpected error occurred. Please try again later.'}
      />
    );
  }

  // Phase 3 - Welcome gate
  if (phase === 'welcome') {
    return (
      <WelcomeGate
        meta={meta}
        onContinue={({ name, consentTimestamp: ts }) => {
          setSignerName(name);
          setConsentTimestamp(ts);
          setPhase('document');
        }}
      />
    );
  }

  // Phase 4 - Document view + field filling
  return (
    <DocumentView
      meta={meta}
      token={token}
      consentTimestamp={consentTimestamp}
      signerName={signerName}
    />
  );
}
